import { useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { HandIcon, RotateCcwIcon, LogOutIcon } from "lucide-react";
import { posthog } from "posthog-js";
import { useSearchParams, useNavigate } from "react-router-dom";

import { AnalysisReportEvents, ConversationPhase } from "shared/types";
import {
  applicationPageVar,
  AuthFlow,
  expertPanelVar,
  isAuthenticatedVar,
  Pages,
} from "@/variables/globalVar";
import { getText } from "shared/lib";

import { useLogout } from "shared/apiHooks";
import {
  useAccount,
  useCanSeeDebugMenu,
  useCaptureEvent,
  useCanSelectPanel,
  CaptureEventType,
  useCanSeeLanguageSelector,
} from "@/hooks";

import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "shared/components";
import { LocalisedLogo } from "@/components/LocalisedLogo";
import { RestartDialog } from "@/components/restart";
import { DebugMenu } from "@/components/debug";
import {
  PostSentToCouncilHelpMessage,
  PreConversationStartedHelpMessage,
  PreSentToCouncilHelpMessage,
} from "@/components/helpMessage";
import { PanelVersionsDropdown } from "./PanelVersionsDropdown";
import { LanguageDropdown } from "./LanguageDropdown";

const REACT_ENV = import.meta.env.VITE_REACT_ENV;

export interface HeaderProps {
  useMyLotLogo?: boolean;
}

export function Header({ useMyLotLogo }: HeaderProps) {
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();

  const { captureEvent } = useCaptureEvent();

  const canSelectPanel = useCanSelectPanel();
  const canSeeDebugMenu = useCanSeeDebugMenu();
  const canSeeLanguageSelector = useCanSeeLanguageSelector();

  const { conversation } = useAccount();

  const isReportComplete =
    conversation?.phase === ConversationPhase.sent_to_council;

  const [isAuthenticated, setIsAuthenticatedVar] = useAtom(isAuthenticatedVar);
  const applicationPage = useAtomValue(applicationPageVar);
  const expertPanel = useAtomValue(expertPanelVar);

  const [restartDialogOpen, setRestartDialogOpen] = useState(false);

  useEffect(() => {
    const handleRestartConversationEvent = () => {
      setRestartDialogOpen(true);
    };

    document.addEventListener(
      AnalysisReportEvents.RESTART_CONVERSATION,
      handleRestartConversationEvent,
    );

    return () => {
      document.removeEventListener(
        AnalysisReportEvents.RESTART_CONVERSATION,
        handleRestartConversationEvent,
      );
    };
  }, []);

  const { mutate } = useLogout({
    onSuccess: () => {
      console.log("<Header> authLogoutList() onSuccess()");

      setSearchParams((prev) => {
        prev.delete("selectedMenuItem");
        return prev;
      });

      captureEvent(CaptureEventType.USER_LOGGED_OUT);

      posthog.reset();

      setIsAuthenticatedVar(false);
      navigate("/login");
    },
    onError: (error) => {
      console.error("<Header> authLogoutList() onError()", error);
    },
  });

  const handleClickLogout = () => {
    mutate();
  };

  return (
    <header className="flex items-center justify-between bg-[#F3F3EE] py-3 pr-3 pl-4 md:h-[60px] md:min-h-[60px] md:py-0 md:pr-9 md:pl-8">
      <div className="relative flex h-[32.51px] w-[73.19px] cursor-pointer items-center justify-center md:h-[36.57px] md:w-[82.34px]">
        <LocalisedLogo useMyLotLogo={useMyLotLogo} />

        {REACT_ENV && REACT_ENV !== "prod" ? (
          <span className="text-textLink text-bodysm absolute top-[-4px] font-bold not-rtl:right-[-35px] rtl:left-[-35px]">
            ({REACT_ENV})
          </span>
        ) : null}
      </div>

      <div className="md:gap:2.5 flex flex-row gap-2.5">
        {isAuthenticated && canSeeDebugMenu ? (
          <div className="mx-1 flex items-center">
            <DebugMenu />
          </div>
        ) : null}

        {isAuthenticated &&
        canSelectPanel &&
        ((applicationPage === Pages.App && expertPanel) ||
          applicationPage === Pages.Address) ? (
          <PanelVersionsDropdown disabled={applicationPage === Pages.App} />
        ) : null}

        {isAuthenticated && applicationPage === Pages.App ? (
          <Dialog open={restartDialogOpen} onOpenChange={setRestartDialogOpen}>
            <DialogTrigger
              tabIndex={0}
              className="text-textSubdued text-bodyxs flex cursor-pointer items-center justify-between gap-1 rounded px-2 py-1.5 md:px-3"
            >
              <RotateCcwIcon className="h-4 w-4" />
              {getText("Restart")}
            </DialogTrigger>
            <RestartDialog
              conversationPhase={
                (conversation?.phase || "") as ConversationPhase
              }
              onOpenChange={setRestartDialogOpen}
            />
          </Dialog>
        ) : null}

        {canSeeLanguageSelector ? (
          <LanguageDropdown
            className={applicationPage === Pages.App ? "hidden" : ""}
          />
        ) : null}

        <Dialog>
          <DialogTrigger
            tabIndex={0}
            className="bg-interfaceWhite border-interactiveBorder text-bodyxs flex cursor-pointer items-center justify-between gap-1 rounded border px-3 py-1.5"
          >
            <HandIcon className="h-4 w-4" />
            {getText("Help")}
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Help</DialogTitle>
            {!conversation ? <PreConversationStartedHelpMessage /> : null}
            {!!conversation && !isReportComplete ? (
              <PreSentToCouncilHelpMessage />
            ) : null}
            {!!conversation && isReportComplete ? (
              <PostSentToCouncilHelpMessage />
            ) : null}
          </DialogContent>
        </Dialog>

        {isAuthenticated ? (
          <button
            className="bg-interfaceWhite border-interactiveBorder text-bodyxs flex cursor-pointer items-center justify-between gap-1 rounded border px-3 py-1.5"
            onClick={handleClickLogout}
          >
            <LogOutIcon className="h-4 w-4" />
            {getText("Log out")}
          </button>
        ) : null}
      </div>
    </header>
  );
}
